import { DialogComponent } from '@theme/dialog';
import { Component } from "@theme/component";
import { morphSection } from '@theme/section-renderer';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import {
  CartAddEvent,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  DiscountUpdateEvent,
  ThemeEvents
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * @typedef {Object} CartItem
 * @property {number} variant_id
 * @property {number} quantity
 * @property {string} [key]
 * @property {string} [title]
 * @property {string} [handle]
 * @property {string} [url]
 * @property {string} [image]
 * @property {number} [price]
 * @property {number} [final_price]
 * @property {number} [line_price]
 * @property {boolean} [requires_shipping]
 * @property {boolean} [gift_card]
 * @property {boolean} [taxable]
 * @property {Object.<string, any>} [properties]
*/

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {TextComponent} cartTotal - The cart total.
 * 
 */
class CartDrawerSortedComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    const url = new URL(window.location.href);
    this.sectionId = "cart-items-json";
    const SECTION_ID_PREFIX = 'shopify-section-';
    this.sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
    url.searchParams.set('sections', `${this.sectionId}`);
    url.searchParams.sort();
    this.url = url;
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    if (this.getAttribute("data-auto-fetch") === "true") {
      this.fetchContent();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.open();
    }
  };

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };


  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    // @ts-ignore
    if (event?.detail?.data?.itemCount === 0) {
      this.showEmptyState();
      return;
    }
    // @ts-ignore
    if (!event?.detail?.data?.sections) return;
    // @ts-ignore
    const data = event.detail.data.sections["cart-items-json"];
    if (!data) return;
    const html = new DOMParser().parseFromString(data, 'text/html');
    const jsonScript = html?.querySelector("script");
    if (!jsonScript) return;
    this.parseCartData(jsonScript);
    this.setSummaryPrice(this.totalWithCurrency);
    this.sortItems();
    this.renderItems();
    this.hideEmptyState();
  };

  open() {
    this.fetchContent();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  fetchContent() {
    if (!this.url) return;
    fetch(this.url).then((response) => {
      return response.json();
    }).then((json) => {
      const html = new DOMParser().parseFromString(json["cart-items-json"], 'text/html');
      const jsonScript = html.querySelector("script");
      if (!jsonScript) throw new Error("No script tag found");
      this.parseCartData(jsonScript);
      if (!this.items || !this.items.length) {
        this.showEmptyState();
        return;
      }
      this.hideEmptyState();
      this.setHeaderQuantity(this.quantity);
      this.setSummaryPrice(this.totalWithCurrency);
      this.sortItems();
      // Temproarily commenting out minimum purchase disclaimer toggle until we can determine a better way to handle this without relying on total price which is not always accurate during zero rated flows
      // this.toggleMinimumPurchaseDisclaimer();
      this.renderItems();
      this.toggleCheckout();
    }).catch((error) => {
      console.error('Error fetching cart drawer content:', error);
      this.showEmptyState();
    });
  }

  /**
     * Parses the cart JSON data
     * @param {HTMLElement} jsonScript - The json script containing cart data
  */
  parseCartData(jsonScript) {
    if (!jsonScript) return;
    const textContent = jsonScript.textContent;
    const jsonContent = JSON.parse(textContent);
    this.quantity = jsonContent.count || 0;
    this.currency = jsonContent.currency || 'USD';
    this.total = jsonContent.total || 0;
    this.totalWithCurrency = jsonContent.total_with_currency || '0.00';
    this.note = jsonContent.note || '';
    this.emptyPrice = jsonContent.empty_price || '0.00';
    this.items = jsonContent.items || [];
    this.checkoutEnabled = jsonContent.checkout_enabled || false;
  }

  toggleMinimumPurchaseDisclaimer() {
    if(!this.refs.minimumPurchaseDisclaimer) return;
    const element = Array.isArray(this.refs.minimumPurchaseDisclaimer) 
      ? this.refs.minimumPurchaseDisclaimer[0] 
      : this.refs.minimumPurchaseDisclaimer;
    if (this.total >= 15000) {
      element?.classList.add("hidden");
    } else {
      element?.classList.remove("hidden");
    }
  }

  toggleCheckout() {
    const checkoutButton = this.querySelector(".cart-drawer__summary .cart__checkout-button");
    if (this.checkoutEnabled) {
      checkoutButton?.removeAttribute("disabled");
    }
    else {
      checkoutButton?.setAttribute("disabled", "true");
    }
  }

  showEmptyState() {
    this.setHeaderQuantity(0);
    this.setSummaryPrice(this.emptyPrice);
    this.querySelector(".cart-drawer__items")?.classList.add("hidden");
    this.querySelector(".cart-drawer__summary")?.classList.add("hidden");
    this.querySelector(".cart-drawer__empty-state")?.classList.remove("hidden");
  }

  hideEmptyState() {
    this.querySelector(".cart-drawer__items")?.classList.remove("hidden");
    this.querySelector(".cart-drawer__summary")?.classList.remove("hidden");
    this.querySelector(".cart-drawer__empty-state")?.classList.add("hidden");
  }

  /**
     * Sets the cart quantity in the cart header
     * @param {Number} quantity - The quantity to set
  */
  setHeaderQuantity(quantity) {
    if (typeof quantity !== 'number') return;
    const quantityElement = this.querySelector(".cart-drawer__header [ref='cartBubbleCount']");
    const quantityDescriptor = this.querySelector(".cart-drawer__header [ref='cartBubbleAriaLabel']");

    if (!quantityElement || !quantityDescriptor) return;
    quantityElement.textContent = String(quantity);
    const currentDescriptor = quantityDescriptor.textContent;
    if (!currentDescriptor) return;
    const descriptorSplit = currentDescriptor.split(':');
    if (descriptorSplit.length < 2) return;
    quantityDescriptor.textContent = `${descriptorSplit[0]}: ${quantity}`;
  }

  /**
     * Sets the final price in the cart footer
     * @param {String} price - The price to set
  */
  setSummaryPrice(price) {
    if (!price || typeof price !== 'string') return;
    const priceElement = this.querySelector(".cart-drawer__summary [ref='cartTotal']");
    if (!priceElement) return;
    priceElement.textContent = price;
    priceElement.setAttribute('value', price);
  }

  /** 
   * Sorts cart items by their line_index property to preserve zero rated config ordering when hot swapping products
   * 
   */
  sortItems() {
    if (!this.items || !Array.isArray(this.items)) return;
    this.items.sort((a, b) => {
      let indexA = a.properties.find((/** @type {any} */property) => property[0] === "_customSortingIndex");
      let indexB = b.properties.find((/** @type {any} */property) => property[0] === "_customSortingIndex");
      indexA = indexA && indexA.length ? parseInt(indexA[1], 10) : 0;
      indexB = indexB && indexB.length ? parseInt(indexB[1], 10) : 0;
      if (indexA < indexB) return -1;
      if (indexA > indexB) return 1;
      return 0;
    });
  }

  renderItems() {
    const container = this.querySelector('.cart-items__wrapper tbody');
    const newContainer = document.createElement("tbody");
    newContainer.setAttribute("role", "rowgroup");
    const wrapper = new DocumentFragment();
    if (!container) return;

    this.items.forEach(
      /**
       * @param {CartItem} item
       */
      (item) => {
        const fragment = new DocumentFragment();
        const cartItem = document.createElement('cart-item-component');
        const dataScript = document.createElement('script');
        dataScript.type = 'application/json';
        dataScript.setAttribute('ref', 'cartItemData');
        dataScript.textContent = JSON.stringify(item);
        cartItem.appendChild(dataScript);
        fragment.appendChild(cartItem);
        wrapper.appendChild(fragment);
      }
    );
    newContainer.append(wrapper);
    const morphOptions = { ...MORPH_OPTIONS };
    morphOptions.fullReplacement = true;
    // We directly call the `morph` function instead of `morphSection` as we're passing in non-string dom trees
    // using `morph` pushes this mutation into the morphing que allowing it to co-exist with existing SSR mutations on cart update
    morph(container, newContainer, morphOptions);
  }

  disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  async getCurrentVariantStock(variantId) {
    if(!variantId) {
      console.error("Variant ID is required to get current stock");
      return null;
    }
    const domain = window.Shopify?.shop || "";
    if(!domain) {
      console.error("Shop domain is required to get current stock");
      return null;
    }

    const graphUrl = `https://${domain}/api/2025-01/graphql.json`;
    const token = '747f691903c5c994f1e2f938559f1bd7';
    const query = `query VariantInventory($id: ID!) { node(id: $id) { __typename ... on ProductVariant { id quantityAvailable } } }`;
    const variantGid = `gid://shopify/ProductVariant/${variantId}`;

    try {
      const response = await fetch(graphUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query: query,
          variables: {
            id: variantGid,
          },
        }),
      });

      const { data, errors } = await response.json();

      if (errors) {
        console.error('GraphQL errors:', errors);
        return null;
      }

      const variant = data.node;
      if (!variant || variant.__typename !== 'ProductVariant') {
        console.error('Node is not a ProductVariant or does not exist');
        return null;
      }

      return variant.quantityAvailable;
    } catch (error) {
      console.error('Failed to fetch variant stock:', error);
      return null;
    }
  }

  /**
   * Handles the zero rated submit event for the Cart items.
   * @param {Number} line - The cart line number
   * @param {Number} quantity - the cart quantity change amount
   * @param {object} [zeroRatedSettings] - Details settings for zero rated variant flows
   * @param {boolean} [zeroRatedSettings.taxable] - Is the current variant a zero rated option
   * @param {Number | null} [zeroRatedSettings.tax_swap_variant_id] - The variant to swap to following zero rated logic
   * @param {Number | null | undefined} [zeroRatedSettings.tax_break_qty] - The quantity threshold controlling zero rated swap logic
   * @param {String} [lineKey] - Line item key
   * @param {String} [customIndex] - Custom sorting index used for zero rated swapping
   * @param {Number} [currentVariantId] - The current variant ID
   * @param {Number} [swapQuantity] - The quantity threshold for swapping to a zero rated variant
   * @param {Boolean} [isDecrease] - Whether the requested quantity is lower than the current in-cart quantity
   */
  async handleZeroRatedChange(line, quantity, zeroRatedSettings, lineKey, customIndex, currentVariantId, swapQuantity, isDecrease) {
    if (!zeroRatedSettings || !lineKey) return;

    const parsedSwapQuantity = swapQuantity ? parseInt(swapQuantity, 10) : null;

    // Only perform stock check when increasing quantity - a decrease can never oversell, so
    // it must not be blocked by an inventory lookup (which can fail if the variant isn't
    // exposed to the Storefront API).
    if (!isDecrease && currentVariantId && parsedSwapQuantity) {
      const currentVariantStock = await this.getCurrentVariantStock(currentVariantId);
      // We need current variant stock to reliably proceed.
      if(currentVariantStock === null) {
        console.error("Unable to update cart, unable to fetch current inventory quantities for variant ID:", currentVariantId);
        this.handleCartError(line, { errors: "Unable to update cart." });
        this.enableCartItems();
        return;
      }

      // If the current available stock is less than the requested quantity  
      // AND the current available stock is less than the swap quantity 
      // we need to stop the flow and prevent adding the NT variant to cart
      if(currentVariantStock < quantity && currentVariantStock < parsedSwapQuantity) {
        this.updateLineQuantity({
          line,
          quantity: currentVariantStock,
          action: 'change',
          notice: `Only ${currentVariantStock} items were added to your cart due to availability.`,
        });
        return;
      }
    }

    // If we're already modifying the zero rated variant and the quantity is above our swap threshold proceed as normal
    // OR if we're modifying a non-zero rated variant and the quantity is below our swap threshold 
    if (zeroRatedSettings.tax_break_qty !== undefined && zeroRatedSettings.tax_break_qty !== null && quantity > zeroRatedSettings.tax_break_qty && !zeroRatedSettings.taxable ||
      zeroRatedSettings.tax_break_qty !== undefined && zeroRatedSettings.tax_break_qty !== null && quantity <= zeroRatedSettings.tax_break_qty && zeroRatedSettings.taxable) {
      this.updateLineQuantity({
        line,
        quantity,
        action: 'change',
      });
    }
    // If we're increasing quantity above the threshold for the zero rated swap but the variant is not currently in our cart we need to swap the variants out
    // OR if we're descreasing quantity below the threshold for the zero rated swap and the zero rated variant is currently in our cart
    else if (zeroRatedSettings.tax_break_qty !== undefined && zeroRatedSettings.tax_break_qty !== null && quantity > zeroRatedSettings.tax_break_qty && zeroRatedSettings.taxable ||
      zeroRatedSettings.tax_break_qty !== undefined && zeroRatedSettings.tax_break_qty !== null && quantity <= zeroRatedSettings.tax_break_qty && !zeroRatedSettings.taxable) {
      this.disableCartItems();
      const updateFormData = new FormData();
      updateFormData.append(
        `updates[${lineKey}]`,
        "0"
      );
      const updateFetchConfig = fetchConfig("javascript", { body: updateFormData });
      // @ts-ignore
      let updateCartRequest = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        ...updateFetchConfig,
      });
      if (!updateCartRequest.ok) {
        console.error("Unable to update cart", updateCartRequest);
        this.handleCartError(line, { errors: "Unable to update cart." });
        this.enableCartItems();
        return;
      }
      if (zeroRatedSettings.tax_swap_variant_id) {
        this.addVariantToCart(zeroRatedSettings.tax_swap_variant_id, quantity, customIndex);
      }
    }
  }

  /**
   * Adds a specified variant ID to cart
   * @param {Number} variantId - The variant ID
   * @param {Number} quantity - the quantity  amount
   * @param {String} [customIndex] - Custom sorting index used for zero rated swapping
   */
  async addVariantToCart(variantId, quantity, customIndex) {
    const { cartTotal } = this.refs;

    const cartComponents = document.querySelectorAll('cart-drawer-sorted-component');
    const sectionsToUpdate = new Set();
    sectionsToUpdate.add(this.dataset.sectionId);
    sectionsToUpdate.add(this.sectionId);

    cartComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const config = fetchConfig("json");
    const formData = {
      items: [
        {
          id: variantId,
          quantity,
          properties: {
            _customSortingIndex: customIndex
          }
        }
      ],
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    }
    // @ts-ignore
    cartTotal?.shimmer();
    const cartPageTotal = this.parentElement?.querySelector(".cart__summary-item text-component");
    if (cartPageTotal) {
      // @ts-ignore
      cartPageTotal.shimmer();
    }
    // @ts-ignore
    const request = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      ...config,
      body: JSON.stringify(formData)
    });
    if (!request.ok) {
      console.error("Unable to add to cart.", request);
      this.fetchContent();
      this.showGenericError("Unable to update cart.");
      return;
    }
    const response = await request.json();
    resetShimmer(this);
    if (response.errors || response.status || response.message) {
      const errors = response.errors || response.message;
      console.error("Error adding to cart.", response);
      this.fetchContent();
      this.showGenericError(`Unable to add to cart: ${errors}`);
      return;
    }

    this.hideGenericError();

    const sectionId = this.dataset.sectionId;

    if (sectionId && response.sections && response.sections[sectionId]) {
      morphSection(sectionId, response.sections[sectionId]);
    }

    this.dispatchEvent(
      new CartUpdateEvent({}, sectionId || "Ajax Cart", {
        itemCount: response.item_count,
        source: 'cart-drawer-sorted-component',
        sections: response.sections,
      })
    );
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.notice] - Optional message shown after the update completes (persists until the next cart update).
   */
  updateLineQuantity(config) {
    if (!config) return;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartComponents = document.querySelectorAll('cart-drawer-sorted-component');
    const sectionsToUpdate = new Set();
    sectionsToUpdate.add(this.dataset.sectionId);
    sectionsToUpdate.add(this.sectionId);

    cartComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    // @ts-ignore
    cartTotal?.shimmer();
    // Shimmer the external total on the cart page
    const cartPageTotal = this.parentElement?.querySelector(".cart__summary-item text-component");
    if (cartPageTotal) {
      // @ts-ignore
      cartPageTotal.shimmer();
    }

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.handleCartError(line, parsedResponseText);
          return;
        }

        this.hideGenericError();

        const sectionId = this.dataset.sectionId;

        if (sectionId && parsedResponseText.sections && parsedResponseText.sections[sectionId]) {
          morphSection(sectionId, parsedResponseText.sections[sectionId]);
        }

        // Surface any notice (e.g. the stock-cap message) *after* the section morph, otherwise
        // the re-render immediately wipes it. It then persists until the next cart update.
        if (config.notice) {
          this.showGenericError(config.notice);
        }

        this.dispatchEvent(
          new CartUpdateEvent({}, sectionId || "Ajax Cart", {
            itemCount: parsedResponseText.item_count,
            source: 'cart-drawer-sorted-component',
            sections: parsedResponseText.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
        this.showGenericError(error.message || "Unable to update cart.");
        resetShimmer(this);
      })
      .finally(() => {
        this.enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  handleCartError = (line, parsedResponseText) => {
    // Removed line level errors for easier cart level refresh handling

    // const quantitySelectors = this.refs.quantitySelectors || Array.from(document.querySelectorAll("cart-drawer-sorted-component quantity-selector-component"));
    // const quantitySelector = Array.isArray(quantitySelectors) ? quantitySelectors[line - 1] : undefined;
    // const quantityInput = quantitySelector?.querySelector('input');

    // if (!quantityInput) throw new Error('Quantity input not found');
    // quantityInput.value = quantityInput.defaultValue;

    // const cartItemError = this.refs[`cartItemError-${line}`] || quantityInput.closest("cart-item-component")?.querySelector(`[ref="cartItemError-${line}"]`);
    // const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`] || quantityInput.closest("cart-item-component")?.querySelector(`[ref="cartItemErrorContainer-${line}"]`);

    // if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    // if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    // cartItemError.textContent = parsedResponseText.errors;
    // cartItemErrorContainer.classList.remove('hidden');
    this.fetchContent();
    this.showGenericError(parsedResponseText.errors || "Unable to update cart.");
  };

  /**
   * Handles generic cart errors - should only pertain to zero rated processing
   * @param {string} [message] - The line.
   */
  showGenericError(message) {
    if (!message) return;
    if (this.refs.errorContainer) {
      if (Array.isArray(this.refs.errorContainer)) {
        this.refs.errorContainer.forEach(el => {
          if (el && 'textContent' in el) el.textContent = message;
          el.classList.remove("hidden");
        });
      } else if ('textContent' in this.refs.errorContainer) {
        this.refs.errorContainer.textContent = message;
        this.refs.errorContainer.classList.remove("hidden");
      }
    }
    resetShimmer(this);
  }

  hideGenericError() {
    if (this.refs.errorContainer) {
      if (Array.isArray(this.refs.errorContainer)) {
        this.refs.errorContainer.forEach(el => {
          el.classList.add("hidden");
        });
      } else if ('textContent' in this.refs.errorContainer) {
        this.refs.errorContainer.classList.add("hidden");
      }
    }
    resetShimmer(this);
  }
}

if (!customElements.get('cart-drawer-sorted-component')) {
  customElements.define('cart-drawer-sorted-component', CartDrawerSortedComponent);
}

class CartItemComponent extends Component {
  requiredRefs = ["cartItemData"];
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'row');
    let cartItemDataRef = this.refs.cartItemData;
    if (Array.isArray(cartItemDataRef)) {
      cartItemDataRef = cartItemDataRef[0];
    }
    this.data = cartItemDataRef && cartItemDataRef.textContent ? JSON.parse(cartItemDataRef.textContent) : null;
    if (!this.data) {
      this.classList.add('hidden');
      return;
    }
    this.cartElement = this.closest("cart-drawer-sorted-component");
    this.renderContent();
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  renderContent() {
    if (!this.data) return;
    const fragment = new DocumentFragment();
    // Media cell
    const media = document.createElement('td');
    media.classList.add("cart-items__media");
    media.setAttribute("headers", "productImage");
    if (this.data.image) {
      const mediaLink = document.createElement('a');
      mediaLink.href = this.data.url || '#';
      mediaLink.classList.add("cart-items__media-container");
      mediaLink.setAttribute("style", `--ratio: ${this.data.image_ratio};`);
      const img = document.createElement('img');
      img.classList.add("cart-items__media-image");
      img.src = this.data.image;
      img.alt = this.data.title || 'Product image';
      // I have not yet set the width/height attributes in the cart JSON. Shopify hardcodes these to 250 so might as well do the same.
      // If we need to use custom values setting the image_width and image_height properties in the JSON will take effect here automatically.
      img.width = this.data.image_width || 250;
      img.height = this.data.image_height || 250;
      mediaLink.appendChild(img);
      media.appendChild(mediaLink);
    }
    fragment.appendChild(media);

    // Details cell
    const details = document.createElement('td');
    details.classList.add("cart-items__details", "cart-primary-typography");
    details.setAttribute("headers", "productInformation");
    const title = document.createElement("p");
    const titleLink = document.createElement('a');
    titleLink.href = this.data.url || '#';
    titleLink.classList.add("cart-items__title");
    titleLink.textContent = this.data.product_title;
    title.appendChild(titleLink);
    details.appendChild(title);

    if (this.data.has_only_default_variant === false) {
      const termList = document.createElement("dl");
      termList.classList.add("cart-items__variants");
      const termWrapper = document.createElement("div");
      termWrapper.classList.add("cart-items__variant");
      /** @type {{name: string, value: string}[]} */
      const optionsWithValues = this.data.options_with_values;
      optionsWithValues.forEach((option, index) => {
        const term = document.createElement("dt");
        term.classList.add("visually-hidden");
        term.textContent = option.name;
        const definition = document.createElement("dd");
        const content = index < optionsWithValues.length - 1 ? `${option.value}, ` : option.value;
        definition.textContent = content;
        termWrapper.appendChild(term);
        termWrapper.appendChild(definition);
      });
      termList.appendChild(termWrapper);
      details.appendChild(termList);
    }

    const priceWrapper = document.createElement("div");
    if (this.data.original_price !== this.data.final_price) {
      const salePriceLabel = document.createElement("span");
      salePriceLabel.classList.add("visually-hidden");
      // @ts-ignore
      salePriceLabel.textContent = window.translationStrings.price_sale;
      priceWrapper.appendChild(salePriceLabel);

      const finalPrice = document.createElement("span");
      finalPrice.textContent = this.data.final_price_money;
      priceWrapper.appendChild(finalPrice);

      const regularPriceLabel = document.createElement("span");
      regularPriceLabel.classList.add("visually-hidden");
      // @ts-ignore
      regularPriceLabel.textContent = window.translationStrings.price_regular;
      priceWrapper.appendChild(regularPriceLabel);

      const comparePrice = document.createElement("s");
      comparePrice.classList.add("compare-at-price");
      if (this.data.compare_at_price > this.data.original_price) {
        comparePrice.textContent = this.data.variant.compare_at_price_money;
      }
      else {
        comparePrice.textContent = this.data.original_price_money;
      }
      priceWrapper.appendChild(comparePrice);
    }
    else {
      const priceLabel = document.createElement("span");
      priceLabel.classList.add("visually-hidden");
      if (this.data.variant.compare_at_price > this.data.original_price) {
        // @ts-ignore
        priceLabel.textContent = window.translationStrings.price_sale;
      }
      else {
        // @ts-ignore
        priceLabel.textContent = window.translationStrings.price;
      }
      priceWrapper.appendChild(priceLabel);

      const originalPrice = document.createElement("span");
      originalPrice.textContent = this.data.original_price_money;
      priceWrapper.appendChild(originalPrice);

      if (this.data.variant.compare_at_price !== this.data.original_price) {
        const regularPriceLabel = document.createElement("span");
        regularPriceLabel.classList.add("visually-hidden");
        const regularPriceContent = document.createElement("s");
        regularPriceContent.classList.add("compare-at-price");
        regularPriceContent.textContent = this.data.variant.compare_at_price_money;
        priceWrapper.appendChild(regularPriceLabel);
        priceWrapper.appendChild(regularPriceContent);
      }
    }
    details.appendChild(priceWrapper);

    fragment.appendChild(details);

    // Quantity cell
    const quantityCell = document.createElement('td');
    quantityCell.classList.add("cart-items__quantity");
    quantityCell.setAttribute("headers", "quantity");

    const quantitySelector = document.createElement("quantity-selector-component");
    quantitySelector.classList.add("quantity-selector");
    quantitySelector.setAttribute("ref", "quantitySelectors[]");
    quantitySelector.setAttribute("data-variant-id", this.data.variant_id);
    quantitySelector.setAttribute("data-swap-quantity", this.data.zero_rated_config?.[0]?.tax_break_qty || '');

    const decreaseButton = document.createElement("button");
    decreaseButton.setAttribute("type", "button");
    decreaseButton.classList.add("button", "quantity-minus", "button-unstyled");
    decreaseButton.setAttribute("name", "minus");
    decreaseButton.setAttribute("on:click", "/decreaseQuantity");
    if (this.data.can_update_quantity === false) {
      decreaseButton.setAttribute("disabled", '');
    }
    const decreaseLabel = document.createElement("span");
    decreaseLabel.classList.add("visually-hidden");
    // @ts-ignore
    decreaseLabel.textContent = window.translationStrings.decrease_quantity;
    decreaseButton.appendChild(decreaseLabel);
    const minusWrapper = document.createElement("span");
    minusWrapper.classList.add("svg-wrapper", "icon-plus");
    // @ts-ignore
    minusWrapper.innerHTML = window.icons.minus;
    decreaseButton.appendChild(minusWrapper);

    quantitySelector.appendChild(decreaseButton);

    const quantityInput = document.createElement("input");
    quantityInput.setAttribute("type", "number")
    quantityInput.setAttribute("name", "quantity");
    quantityInput.value = this.data.quantity || 1;
    quantityInput.defaultValue = this.data.quantity || 1;
    quantityInput.setAttribute("min", String(1));
    quantityInput.setAttribute("on:blur", "/setQuantity");
    quantityInput.setAttribute("on:focus", "/selectInputValue");
    quantityInput.setAttribute("ref", "quantityInput");
    quantityInput.setAttribute("data-cart-line", this.data.line_index);
    // @ts-ignore
    quantityInput.setAttribute("aria-label", window.translationStrings.quantity);
    if (this.data.variant.quantity_rule.max) {
      quantityInput.setAttribute("max", this.data.variant.quantity_rule.max);
    }
    quantityInput.setAttribute("step", this.data.variant.quantity_rule.increment || 1);
    if (this.data.can_update_quantity === false || this.data.variant.available === false) {
      quantityInput.setAttribute("disabled", '');
    }

    quantitySelector.appendChild(quantityInput);

    const increaseButton = document.createElement("button");
    increaseButton.setAttribute("type", "button");
    increaseButton.classList.add("button", "quantity-plus", "button-unstyled");
    increaseButton.setAttribute("name", "plus");
    increaseButton.setAttribute("on:click", "/increaseQuantity");
    if (this.data.can_update_quantity === false) {
      increaseButton.setAttribute("disabled", '');
    }
    const increaseLabel = document.createElement("span");
    increaseLabel.classList.add("visually-hidden");
    // @ts-ignore
    increaseLabel.textContent = window.translationStrings.increase_quantity;
    increaseButton.appendChild(increaseLabel);
    const plusWrapper = document.createElement("span");
    plusWrapper.classList.add("svg-wrapper", "icon-plus");
    // @ts-ignore
    plusWrapper.innerHTML = window.icons.plus;
    increaseButton.appendChild(plusWrapper);

    quantitySelector.appendChild(increaseButton);

    quantityCell.appendChild(quantitySelector);

    const removeButton = document.createElement("button");
    removeButton.setAttribute("type", "button");
    removeButton.classList.add("button", "button--tertiary", "cart-items__remove");
    // @ts-ignore
    removeButton.setAttribute("aria-label", window.translationStrings.remove);
    removeButton.setAttribute("on:click", `/onLineRemove/${this.data.line_index}`);
    if (this.data.can_remove === false) {
      removeButton.setAttribute("hidden", '');
    }
    // @ts-ignore
    removeButton.innerHTML = window.icons.delete;
    const removeButtonLabel = document.createElement("span");
    removeButtonLabel.classList.add("visually-hidden");
    // @ts-ignore
    removeButtonLabel.textContent = window.translationStrings.remove
    removeButton.appendChild(removeButtonLabel);

    quantityCell.appendChild(removeButton);

    fragment.appendChild(quantityCell);

    // Price cell
    const priceCell = document.createElement('td');
    priceCell.classList.add("cart-items__price", "cart-secondary-typography");
    priceCell.setAttribute("headers", "productTotal");
    const finalPriceWrapper = document.createElement("text-component");
    let finalPrice;
    if (this.data.currency_code_enabled_cart_items) {
      finalPrice = this.data.final_line_price_with_currency;
    }
    else {
      finalPrice = this.data.final_line_price;
    }
    finalPriceWrapper.setAttribute("value", finalPrice);
    finalPriceWrapper.textContent = finalPrice;
    priceCell.appendChild(finalPriceWrapper);

    if (this.data.unit_price_measurement) {
      const unitWrapper = document.createElement("div");
      unitWrapper.classList.add("cart-items__price-unit", "cart-secondary-typography");

      const unitText = document.createElement("small");
      unitText.classList.add("unit-price");
      const unitLabel = document.createElement("span");
      if (this.data.currency_code_enabled_cart_items) {
        unitLabel.textContent = this.data.final_unit_price_with_currency_and_measurement;
      }
      else {
        unitLabel.textContent = this.data.final_unit_price_with_measurement;
      }
      unitLabel.classList.add("visually-hidden");
      // @ts-ignore
      unitLabel.textContent = window.translationStrings.unit_price
      unitText.prepend(unitLabel);

      priceCell.appendChild(unitWrapper);
    }

    fragment.appendChild(priceCell);

    const errorContainer = document.createElement("div");
    errorContainer.classList.add("cart-items__error", "hidden");
    errorContainer.setAttribute("ref", `cartItemErrorContainer-${this.data.line_index}`);
    const errorMessage = document.createElement("p");
    errorMessage.setAttribute("role", "alert");
    errorMessage.classList.add("cart-item__error");
    const errorText = document.createElement("strong");
    errorText.setAttribute("ref", `cartItemError-${this.data.line_index}`);
    errorText.classList.add("cart-item__error-text");
    errorMessage.appendChild(errorText);
    errorContainer.appendChild(errorMessage);
    fragment.appendChild(errorContainer);

    this.appendChild(fragment);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
  */
  #onQuantityChange(event) {
    const { quantity, cartLine: line, variantId, swapQuantity } = event.detail;
    if (typeof line === 'undefined' || line !== this.data.line_index) return;

    if (quantity == 0) {
      this.onLineRemove(line);
      return;
    }

    const isDecrease = quantity < this.data.quantity;

    const textComponent = /** @type {TextComponent | undefined} */ (this.querySelector('text-component'));
    textComponent?.shimmer();

    if (this.data.zero_rated_config && this.data.zero_rated_config.length) {
      let customIndex = this.data.properties.find((/** @type {any} */property) => {
        return property[0] === "_customSortingIndex"
      });
      customIndex = customIndex && customIndex.length ? customIndex[1].toString() : "0";
      const config = /** @type {any} */ ({ ...this.data.zero_rated_config[0] });
      const lineKey = this.data.key;
      (/** @type {any} */ (this.cartElement)).handleZeroRatedChange?.(
        line,
        quantity,
        config,
        lineKey,
        customIndex,
        variantId,
        swapQuantity,
        isDecrease
      );
      return;
    }

    if (!this.cartElement) return;
    (/** @type {any} */ (this.cartElement)).updateLineQuantity?.({
      line,
      quantity,
      action: 'change',
    });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
  */
  onLineRemove(line) {
    if (!this.cartElement) return;
    (/** @type {any} */ (this.cartElement)).updateLineQuantity?.({
      line,
      quantity: 0,
      action: 'clear',
    });
  }
}

if (!customElements.get('cart-item-component')) {
  customElements.define('cart-item-component', CartItemComponent);
}
